import React, { useCallback, useRef } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Popup } from "@progress/kendo-react-popup";
import useOnClickOutside from "use-onclickoutside";
import { icon } from "../../../../config";
import { WORKFLOW_VARIABLE_LABELS } from "../workflowVariables.constants";

const ANCHOR_ALIGN = { horizontal: "left", vertical: "bottom" };
const POPUP_ALIGN = { horizontal: "left", vertical: "top" };

/** @param {EventTarget | null} target */
const isKendoOverlayTarget = (target) => {
  if (!target || typeof target.closest !== "function") return false;
  return Boolean(
    target.closest(".k-popup") ||
      target.closest(".k-animation-container") ||
      target.closest(".k-child-animation-container") ||
      target.closest(".k-calendar") ||
      target.closest(".k-datetimepicker")
  );
};

/**
 * Shared Kendo Popup shell: header (Filter + type icon), body slot, Filter/Clear footer.
 */
const MultiDimensionColumnFilterPopupShell = ({ anchorRef, typeIcon, onClose, onApply, onClear, children, applyDisabled = false, cardClassName = "" }) => {
  const panelRef = useRef(null);

  const hideOnClickOutside = useCallback(
    (e) => {
      if (isKendoOverlayTarget(e?.target)) return;
      onClose();
    },
    [onClose]
  );

  useOnClickOutside(panelRef, hideOnClickOutside);

  const anchor = anchorRef?.current;
  if (!anchor) return null;

  return (
    <Popup
      anchor={anchor}
      show
      animate
      anchorAlign={ANCHOR_ALIGN}
      popupAlign={POPUP_ALIGN}
      collision={{ horizontal: "fit", vertical: "flip" }}
      className='multi-dimension-column-popup-root'
      popupClass='multi-dimension-column-popup-kendo-inner'>
      <div
        ref={panelRef}
        className={`shadow multi-dimension-column-popup-card multi-dimension-filter-popup-card ${cardClassName}`}
        role='dialog'
        aria-labelledby='multi-dim-filter-dialog-title'>
        <div className='card-body p-3'>
          <div className='d-flex align-items-center justify-content-between pb-2 mb-2 border-bottom'>
            <span className='fw-bold' id='multi-dim-filter-dialog-title'>
              {WORKFLOW_VARIABLE_LABELS.MULTI_DIMENSION_FILTER_TITLE}
            </span>
            <span className='d-inline-flex align-items-center text-muted' aria-hidden>
              {typeIcon}
            </span>
          </div>

          {children}

          <div className='d-flex justify-content-between gap-2 mt-3 pt-2 border-top'>
            <Button themeColor='primary' className='flex-grow-1' onClick={onApply} disabled={applyDisabled}>
              {WORKFLOW_VARIABLE_LABELS.MULTI_DIMENSION_FILTER_APPLY}
            </Button>
            <Button fillMode='outline' className='flex-grow-1' onClick={onClear}>
              {WORKFLOW_VARIABLE_LABELS.MULTI_DIMENSION_FILTER_CLEAR}
            </Button>
          </div>
        </div>
      </div>
    </Popup>
  );
};

export default MultiDimensionColumnFilterPopupShell;
